import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BREAK_LABELS = {
  tea_morning: { emoji: '☕', label: 'Morning Tea (15 min)', sub: 'Morning Tea', limit: 15 },
  lunch:       { emoji: '🍱', label: 'Lunch (45 min)',       sub: 'Lunch',        limit: 45 },
  tea_evening: { emoji: '☕', label: 'Evening Tea (15 min)', sub: 'Evening Tea',  limit: 15 },
};

function fmtDuration(min) {
  if (!min) return '0 min';
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}h ${m}m` : `${m} min`;
}

// mm:ss format for live break countdown
function fmtMmSs(totalSeconds) {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Live break-elapsed component — ticks every second while a break is open.
function BreakTimer({ startIso, limit }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!startIso) return null;
  const startMs = new Date(startIso).getTime();
  const elapsedSec = Math.max(0, Math.floor((now - startMs) / 1000));
  const limitSec = (limit || 0) * 60;
  const remaining = limitSec - elapsedSec;
  const overtime = remaining < 0;
  return (
    <div
      className="text-center font-mono font-black"
      style={{ color: overtime ? '#b91c1c' : '#16a34a', fontSize: 18, letterSpacing: 1 }}
      data-testid="break-timer"
    >
      {overtime ? `OVER +${fmtMmSs(-remaining)}` : fmtMmSs(remaining)}
    </div>
  );
}

const MyAttendancePage = () => {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);     // /api/my/checkin/today response
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSelfie, setShowSelfie] = useState(null);  // null | 'in' | 'out'
  const [selfieData, setSelfieData] = useState('');
  const [gps, setGps] = useState(null);
  const [recent, setRecent] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  useEffect(() => () => stopCamera(), []);

  // GPS once
  useEffect(() => {
    if (gps || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 },
    );
  }, [gps]);

  const fetchStatus = async () => {
    setError('');
    try {
      const [t, r] = await Promise.all([
        axios.get(`${API_URL}/api/my/checkin/today`, { withCredentials: true }),
        axios.get(`${API_URL}/api/my/checkin/recent`, { withCredentials: true, params: { days: 14 } }).catch(() => ({ data: [] })),
      ]);
      setData(t.data);
      setRecent(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };
  useEffect(() => { fetchStatus(); /* eslint-disable-next-line */ }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 640 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      setError('Camera access denied. ' + e.message);
      setShowSelfie(null);
    }
  };
  const capture = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = 320; c.height = 426;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    setSelfieData(c.toDataURL('image/jpeg', 0.72));
    stopCamera();
  };
  const openSelfieFor = (action) => {
    setShowSelfie(action);
    setSelfieData('');
    setTimeout(startCamera, 100);
  };

  const submitWithSelfie = async (action) => {
    setBusy(true); setError('');
    try {
      const body = { selfie: selfieData, gps };
      const url = `${API_URL}/api/my/checkin/${action === 'in' ? 'in' : 'out'}`;
      await axios.post(url, body, { withCredentials: true });
      setShowSelfie(null); setSelfieData(''); stopCamera();
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // Skip selfie path (e.g. desktop with no camera permission)
  const punchPlain = async (action) => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/api/my/checkin/${action === 'in' ? 'in' : 'out'}`, { gps }, { withCredentials: true });
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const doBreak = async (action, type) => {
    // Confirm with the staff member before firing — a single accidental click on
    // a coffee/lunch tile shouldn't be enough to start (or end) a break.
    const label = BREAK_LABELS[type]?.sub || type;
    const limit = BREAK_LABELS[type]?.limit || 0;
    const msg = action === 'start'
      ? `Start your ${label} break now?\n\nYour timer will run with a ${limit}-minute limit.\nYou won't be able to start a different break until this one ends.`
      : `End your ${label} break now?\n\nYour break time will be saved against today's attendance.`;
    if (!window.confirm(msg)) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/api/my/checkin/break/${action}`, { type }, { withCredentials: true });
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // ------------------------- RENDER -------------------------
  if (data && data.linked === false) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md p-8 text-center space-y-3 border-2 border-amber-200">
          <div className="text-5xl">🔗</div>
          <div className="text-lg font-bold text-amber-800">Not linked to an employee record</div>
          <p className="text-sm text-gray-600">Your login isn't linked to an employee profile yet. Please ask your admin to link your account on the Users page so you can mark attendance.</p>
        </div>
      </div>
    );
  }

  const rec = data?.record;
  const checkedIn = !!rec?.check_in;
  const checkedOut = !!rec?.check_out;
  const breaks = rec?.breaks || [];
  const openBreakType = rec?.open_break || null;
  const isWorking = checkedIn && !checkedOut && !openBreakType;
  const isDone = checkedOut;
  const breaksUsed = breaks.filter((b) => b.end).length;

  // ===== Selfie modal =====
  if (showSelfie) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4" data-testid="selfie-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
          <div className="text-center">
            <div className="text-base font-bold" style={{ color: '#0f172a' }}>
              📸 Capture Selfie for {showSelfie === 'in' ? 'Check IN' : 'Check OUT'}
            </div>
            <div className="text-[10px] mt-0.5 text-gray-500">Look at the camera and tap Capture</div>
          </div>
          <div className="rounded-lg overflow-hidden bg-black aspect-[3/4] flex items-center justify-center">
            {selfieData
              ? <img src={selfieData} alt="captured" className="w-full h-full object-cover" />
              : <video ref={videoRef} className="w-full h-full object-cover" playsInline muted data-testid="selfie-video" />
            }
          </div>
          <canvas ref={canvasRef} className="hidden" />
          {error && <div className="text-xs px-3 py-2 rounded-md bg-rose-50 text-rose-700">{error}</div>}
          <div className="flex gap-2">
            <button onClick={() => { setShowSelfie(null); stopCamera(); setSelfieData(''); }}
              className="flex-1 py-2.5 rounded-lg font-semibold text-sm border border-gray-200 text-gray-600">
              Cancel
            </button>
            {!selfieData ? (
              <button onClick={capture} data-testid="capture-btn"
                className="flex-1 py-2.5 rounded-lg font-bold text-sm text-white bg-slate-900">
                📷 Capture
              </button>
            ) : (
              <>
                <button onClick={() => { setSelfieData(''); startCamera(); }}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-sm border border-gray-200">
                  Retake
                </button>
                <button onClick={() => submitWithSelfie(showSelfie)} disabled={busy} data-testid="confirm-checkin-btn"
                  className={`flex-1 py-2.5 rounded-lg font-bold text-sm text-white ${showSelfie === 'in' ? 'bg-emerald-700' : 'bg-amber-700'}`}>
                  {busy ? 'Sending…' : (showSelfie === 'in' ? '✓ Confirm IN' : '✓ Confirm OUT')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== Main dashboard =====
  return (
    <div className="min-h-[80vh] p-4" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#334155 100%)', borderRadius: 14, margin: '-1.5rem' }} data-testid="my-attendance-page">
      <div className="max-w-md mx-auto space-y-3 py-6">
        {/* Welcome header */}
        <div className="bg-white rounded-2xl shadow-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-400">Welcome</div>
              <div className="text-lg font-bold" style={{ color: '#0f172a' }}>{data?.name || user?.name}</div>
              <div className="text-xs font-mono text-gray-500">{data?.emp_id || '—'} · {data?.today}</div>
            </div>
            <button onClick={logout} className="text-[10px] underline text-gray-400" data-testid="switch-user-btn">
              Switch user
            </button>
          </div>
        </div>

        {/* State: not checked in */}
        {!checkedIn && (
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center space-y-3">
            <div className="text-base font-bold" style={{ color: '#0f172a' }}>Ready to start your day?</div>
            <div className="text-xs text-gray-500">Tap below to capture your selfie and check in</div>
            <button onClick={() => openSelfieFor('in')} disabled={busy} data-testid="check-in-btn"
              className="w-full py-4 rounded-xl text-white font-bold text-base uppercase tracking-wide bg-emerald-700">
              ✓ Check IN
            </button>
            <button onClick={() => punchPlain('in')} disabled={busy} className="text-[11px] underline text-gray-400">
              Skip selfie (desktop / no camera)
            </button>
          </div>
        )}

        {/* State: checked in, working / on break */}
        {checkedIn && !checkedOut && (
          <div className="bg-white rounded-2xl shadow-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-400">Checked In At</div>
                <div className="text-2xl font-black font-mono text-emerald-700">{rec.check_in}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-gray-400">Breaks Used</div>
                <div className="text-base font-bold" style={{ color: '#0f172a' }}>{breaksUsed} / 3</div>
              </div>
            </div>

            {/* Break tiles */}
            <div className="grid grid-cols-3 gap-2 pt-2">
              {Object.entries(BREAK_LABELS).map(([key, info]) => {
                const b = breaks.find((x) => x.type === key);
                const done = b && b.end;
                const open = b && !b.end;
                const disabled = busy || done || (openBreakType && !open);
                return (
                  <button
                    key={key}
                    disabled={disabled}
                    onClick={() => doBreak(open ? 'end' : 'start', key)}
                    data-testid={`break-${key}-btn`}
                    className="px-2 py-3 rounded-lg text-[10px] font-bold text-center disabled:opacity-40 transition-all"
                    style={{
                      background: done ? '#dcfce7' : open ? '#fde68a' : '#f1f5f9',
                      color: done ? '#15803d' : open ? '#92400e' : '#334155',
                      border: open ? '2px solid #d97706' : '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ fontSize: '20px', marginBottom: 2 }}>{info.emoji}</div>
                    <div>{done ? '✓ DONE' : open ? '⏸ END BREAK' : info.sub}</div>
                    {open && (
                      <BreakTimer startIso={b.start_iso} limit={info.limit} />
                    )}
                    {done && b.elapsed_minutes != null && (
                      <div
                        className="text-[10px] font-mono mt-1"
                        style={{ color: b.overtime_minutes > 0 ? '#b91c1c' : '#15803d' }}
                        data-testid={`break-${key}-elapsed`}
                      >
                        {b.elapsed_minutes}m{b.overtime_minutes > 0 ? ` (+${b.overtime_minutes})` : ''}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Open break overtime alert */}
            {openBreakType && (() => {
              const ob = breaks.find((x) => x.type === openBreakType);
              if (!ob) return null;
              const lim = BREAK_LABELS[openBreakType]?.limit || 0;
              if (!lim || (ob.elapsed_minutes || 0) <= lim) return null;
              return (
                <div className="mt-2 px-3 py-2 rounded-lg text-xs text-rose-700 bg-rose-50 border border-rose-200">
                  <strong>⚠ You're over the {lim}-minute limit by {(ob.elapsed_minutes || 0) - lim} minute(s).</strong> Please end your break.
                </div>
              );
            })()}
          </div>
        )}

        {/* Check-out (only when no open break) — selfie is MANDATORY, no skip */}
        {isWorking && (
          <div className="bg-white rounded-2xl shadow-xl p-5 text-center space-y-2">
            <div className="text-xs text-gray-500">Done for the day?</div>
            <button onClick={() => openSelfieFor('out')} disabled={busy} data-testid="check-out-btn"
              className="w-full py-3.5 rounded-xl text-white font-bold text-sm uppercase tracking-wide bg-amber-700">
              ✗ Check OUT (Face scan required)
            </button>
            <div className="text-[10px] text-gray-400 mt-1">Face scan is mandatory at checkout</div>
          </div>
        )}

        {/* All done */}
        {isDone && (
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center space-y-3">
            <div className="text-4xl">🎉</div>
            <div className="text-base font-bold" style={{ color: '#0f172a' }}>All done for today!</div>
            <div className="grid grid-cols-2 gap-3 pt-2 text-left">
              <div className="rounded-lg p-2.5 bg-emerald-50">
                <div className="text-[9px] uppercase font-bold text-emerald-700">Check In</div>
                <div className="text-sm font-mono font-bold">{rec.check_in}</div>
              </div>
              <div className="rounded-lg p-2.5 bg-amber-50">
                <div className="text-[9px] uppercase font-bold text-amber-700">Check Out</div>
                <div className="text-sm font-mono font-bold">{rec.check_out}</div>
              </div>
              <div className="rounded-lg p-2.5 bg-blue-50">
                <div className="text-[9px] uppercase font-bold text-blue-800">Work Time</div>
                <div className="text-sm font-bold">{Number(rec.total_hours).toFixed(2)}h</div>
              </div>
              <div className="rounded-lg p-2.5 bg-purple-50">
                <div className="text-[9px] uppercase font-bold text-purple-800">Break Time</div>
                <div className="text-sm font-bold">{fmtDuration(rec.break_minutes)}</div>
              </div>
            </div>
            <div className="text-[10px] text-gray-400">See you tomorrow at 10:30 AM</div>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-xl p-3 text-xs text-rose-700">{error}</div>
        )}

        {/* Last 14 days */}
        <div className="bg-white rounded-2xl shadow-xl p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Last 14 days</div>
          <table className="w-full text-[11px]">
            <thead className="text-[9px] uppercase text-gray-400">
              <tr className="border-b">
                <th className="text-left py-1">Date</th>
                <th className="text-left">In</th>
                <th className="text-left">Out</th>
                <th className="text-left">Worked</th>
                <th className="text-left">Breaks</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && <tr><td colSpan="5" className="py-3 text-center text-gray-300 italic">No records yet.</td></tr>}
              {recent.map((r) => (
                <tr key={r.id || r.date} className="border-b last:border-0">
                  <td className="py-1.5">{r.date}</td>
                  <td className="font-mono">{r.check_in || '—'}</td>
                  <td className="font-mono">{r.check_out || '—'}</td>
                  <td>{r.total_hours ? Number(r.total_hours).toFixed(2) + 'h' : '—'}</td>
                  <td>{r.break_minutes ? fmtDuration(r.break_minutes) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-center text-[10px] text-gray-400 pt-2">
          Dextra Technologies · Daily Attendance Portal
        </div>
      </div>
    </div>
  );
};

export default MyAttendancePage;
